import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Loader2, MapPin, ShoppingBag, Tag, Trash2, Banknote, CreditCard } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { trackInitiateCheckout, trackPurchase } from "@/lib/metaPixel";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSettings } from "@/contexts/SettingsContext";
import { openRazorpay } from "@/lib/razorpay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Checkout = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { items, remove, subtotal, clear } = useCart();
    const { settings } = useSettings();
    const [address, setAddress] = useState({
        full_name: user?.name || "",
        phone: user?.phone || "",
        email: user?.email || "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        pincode: "",
        country: "India",
    });
    const [coupon, setCoupon] = useState("");
    const [discount, setDiscount] = useState(0);
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("razorpay"); // "razorpay" | "cod"
    const [miniCodEnabled, setMiniCodEnabled] = useState(false);

    // Guest checkout allowed — no login required.

    // Miniature items: re-check the CURRENT admin COD setting instead of the stale
    // cod_available flag stamped on the cart item when it was added.
    const hasMiniature = items.some((it) => it.product_id?.startsWith("mini:"));
    useEffect(() => {
        if (!hasMiniature) return;
        api.get("/miniature/config").then((r) => setMiniCodEnabled(!!r.data?.cod_enabled)).catch(() => {});
        /* eslint-disable-next-line */
    }, [hasMiniature]);

    // COD is available only when every cart item has it enabled
    const codAllowed = items.length > 0 && items.every((it) =>
        it.cod_available === true || (it.product_id?.startsWith("mini:") && miniCodEnabled)
    );

    const gstPercent = settings?.pricing?.gst_percent ?? 18;
    const shipping = settings?.pricing?.default_shipping_inr ?? 0;
    const taxable = Math.max(0, subtotal - discount);
    const gst = Math.round(taxable * gstPercent) / 100; // exact paise — matches backend rounding
    const total = Math.round((taxable + shipping + gst) * 100) / 100;

    const firedCheckoutEvent = useRef(false);
    useEffect(() => {
        if (!firedCheckoutEvent.current && items.length > 0) {
            firedCheckoutEvent.current = true;
            trackInitiateCheckout({
                value: total,
                numItems: items.length,
                contentIds: items.map((it) => it.product_id),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    const applyCoupon = async () => {
        if (!coupon) return;
        try {
            const { data } = await api.post("/orders/coupon/validate", { code: coupon, subtotal_inr: subtotal });
            setDiscount(data.discount_inr);
            toast.success(`Coupon applied · You saved ${formatINR(data.discount_inr)}`);
        } catch (e) {
            setDiscount(0);
            toast.error(e.response?.data?.detail || "Invalid coupon");
        }
    };

    const placeOrder = async () => {
        if (items.length === 0) { toast.error("Cart is empty"); return; }
        if (!address.full_name || !address.phone || !address.line1 || !address.pincode) { toast.error("Please fill your shipping address"); return; }
        const method = codAllowed ? paymentMethod : "razorpay";
        setLoading(true);
        try {
            const { data: order } = await api.post("/orders/checkout", {
                items,
                address,
                coupon_code: discount ? coupon : null,
                notes,
                payment_method: method,
            });
            if (method === "cod") {
                confetti({ particleCount: 200, spread: 90, origin: { y: 0.5 } });
                toast.success(`Order placed! ${order.order_number} — please check your email to confirm COD`);
                trackPurchase({
                    value: order.total_inr,
                    orderId: order.order_number,
                    contentIds: items.map((it) => it.product_id),
                });
                clear();
                navigate(`/track?number=${order.order_number}${user ? "" : "&welcome=1"}`);
                return;
            }
            if ((order.total_inr || 0) <= 0) {
                // Free order (admin/team miniature) — no payment window needed
                confetti({ particleCount: 200, spread: 90, origin: { y: 0.5 } });
                toast.success(`Order placed! ${order.order_number}`);
                trackPurchase({ value: 0, orderId: order.order_number, contentIds: items.map((it) => it.product_id) });
                clear();
                navigate(`/track?number=${order.order_number}${user ? "" : "&welcome=1"}`);
                return;
            }
            const { data: rzp } = await api.post("/payments/order/create", { order_id: order.id });
            await openRazorpay({
                keyId: rzp.key_id,
                orderId: rzp.razorpay_order_id,
                amountPaise: rzp.amount_paise,
                description: `Order ${order.order_number}`,
                prefill: rzp.user,
                onDismiss: () => { setLoading(false); toast.info("Payment cancelled — your order is saved."); navigate(`/track?number=${order.order_number}`); },
                onSuccess: async (response) => {
                    try {
                        await api.post("/payments/order/verify", {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            order_id: order.id,
                        });
                        confetti({ particleCount: 200, spread: 90, origin: { y: 0.5 } });
                        toast.success(`Order placed! ${order.order_number}`);
                        trackPurchase({
                            value: order.total_inr,
                            orderId: order.order_number,
                            contentIds: items.map((it) => it.product_id),
                        });
                        clear();
                        navigate(`/track?number=${order.order_number}${user ? "" : "&welcome=1"}`);
                    } catch (e) {
                        toast.error("Payment verification failed");
                        setLoading(false);
                    }
                },
            });
        } catch (e) {
            setLoading(false);
            toast.error(e.response?.data?.detail || "Checkout failed");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10" data-testid="checkout-page">
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Checkout</h1>
            <p className="text-muted-foreground mt-1">Review your cart and complete your order securely.</p>

            {items.length === 0 ? (
                <div className="mt-10 rounded-4xl glass p-14 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3"><ShoppingBag className="w-6 h-6 text-primary" /></div>
                    <div className="font-display text-xl">Your cart is empty</div>
                    <p className="text-sm text-muted-foreground mt-1">Explore the collection to add your favourite miniature.</p>
                    <Button onClick={() => navigate("/shop")} className="rounded-full mt-5 bg-primary hover:bg-primary/90">Browse the shop</Button>
                </div>
            ) : (
                <div className="mt-8 grid lg:grid-cols-[1fr_400px] gap-8">
                    <div className="space-y-6">
                        {/* Cart items */}
                        <div className="rounded-3xl glass p-5">
                            <h3 className="font-display text-lg font-semibold mb-4">Your items</h3>
                            <div className="space-y-3">
                                {items.map((it, idx) => (
                                    <div key={idx} className="flex gap-4 p-3 rounded-2xl bg-white/40" data-testid={`cart-item-${idx}`}>
                                        <div className="w-24 h-24 rounded-xl bg-brand-cream overflow-hidden shrink-0 flex items-center justify-center">
                                            {(it.reference_photos && it.reference_photos[0]) ? (
                                                <img src={it.reference_photos[0].startsWith("http") ? it.reference_photos[0] : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${it.reference_photos[0]}`} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="text-muted-foreground text-[10px] text-center px-1">Photos on WhatsApp</div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-semibold text-sm">{it.product_name}</div>
                                            <div className="text-xs text-muted-foreground">{it.size_label} · Qty {it.quantity || 1}</div>
                                            {it.selected_options && Object.keys(it.selected_options).length > 0 && (
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {Object.entries(it.selected_options).map(([k, v]) => (
                                                        <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary" data-testid={`cart-opt-${idx}-${k}`}>{k}: {v}</span>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="text-sm font-semibold text-primary mt-1">{formatINR((it.unit_price_inr || 0) * (it.quantity || 1))}</div>
                                        </div>
                                        <button onClick={() => remove(idx)} className="text-destructive hover:opacity-70 self-start" data-testid={`cart-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Address */}
                        <div className="rounded-3xl glass p-5">
                            <div className="flex items-center gap-2 mb-4"><MapPin className="w-4 h-4 text-primary" /><h3 className="font-display text-lg font-semibold">Shipping address</h3></div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <Input placeholder="Full name" value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} data-testid="ship-name" className="rounded-xl h-11 bg-white/50" />
                                <Input placeholder="Phone" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} data-testid="ship-phone" className="rounded-xl h-11 bg-white/50" />
                                <Input placeholder="Email" type="email" value={address.email} onChange={(e) => setAddress({ ...address, email: e.target.value })} className="rounded-xl h-11 bg-white/50 md:col-span-2" />
                                <Input placeholder="Address line 1" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} data-testid="ship-line1" className="rounded-xl h-11 bg-white/50 md:col-span-2" />
                                <Input placeholder="Address line 2 (optional)" value={address.line2} onChange={(e) => setAddress({ ...address, line2: e.target.value })} className="rounded-xl h-11 bg-white/50 md:col-span-2" />
                                <Input placeholder="City" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                                <Input placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                                <Input placeholder="Pincode" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} data-testid="ship-pincode" className="rounded-xl h-11 bg-white/50" />
                                <Input placeholder="Country" value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="rounded-xl h-11 bg-white/50" />
                            </div>
                            <textarea placeholder="Order notes (optional) — engraving text, delivery date, etc." value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-3 w-full rounded-2xl border border-input bg-white/50 p-3 text-sm min-h-[80px]" />
                        </div>
                    </div>

                    {/* Summary */}
                    <aside>
                        <div className="rounded-3xl glass-strong p-6 sticky top-28">
                            <h3 className="font-display text-lg font-semibold mb-4">Order summary</h3>
                            <div className="flex items-center gap-2 mb-4">
                                <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="coupon-input" className="rounded-full h-10 bg-white/60" />
                                <Button onClick={applyCoupon} type="button" variant="outline" className="rounded-full h-10 border-primary/40 text-primary" data-testid="apply-coupon"><Tag className="w-3.5 h-3.5" /> Apply</Button>
                            </div>
                            <div className="space-y-2 text-sm">
                                <Row label="Subtotal" value={formatINR(subtotal)} />
                                {discount > 0 && <Row label={`Discount (${coupon})`} value={`− ${formatINR(discount)}`} pos />}
                                <Row label={`GST (${gstPercent}%)`} value={formatINR(gst)} />
                                <Row label="Shipping" value={shipping ? formatINR(shipping) : "Free"} />
                                <div className="border-t border-border pt-3 flex justify-between items-baseline">
                                    <div className="text-sm">Total</div>
                                    <div className="font-display text-2xl font-bold text-primary" data-testid="order-total">{formatINR(total)}</div>
                                </div>
                            </div>

                            {/* Payment method selector — shown only when every cart item supports COD */}
                            {codAllowed && (
                                <div className="mt-4" data-testid="payment-method-picker">
                                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Payment method</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod("razorpay")}
                                            data-testid="pay-razorpay-btn"
                                            className={`rounded-2xl border p-3 text-left transition ${paymentMethod === "razorpay" ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}
                                        >
                                            <CreditCard className="w-4 h-4 mb-1 text-primary" />
                                            <div className="text-xs font-semibold">Pay Online</div>
                                            <div className="text-[10px] text-muted-foreground">Card, UPI, NetBanking</div>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPaymentMethod("cod")}
                                            data-testid="pay-cod-btn"
                                            className={`rounded-2xl border p-3 text-left transition ${paymentMethod === "cod" ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}
                                        >
                                            <Banknote className="w-4 h-4 mb-1 text-primary" />
                                            <div className="text-xs font-semibold">Cash on Delivery</div>
                                            <div className="text-[10px] text-muted-foreground">Pay when it arrives</div>
                                        </button>
                                    </div>
                                </div>
                            )}
                            {!codAllowed && items.length > 0 && (
                                <div className="mt-3 text-[10px] text-muted-foreground text-center italic">
                                    Online payment only — COD not available for one or more items.
                                </div>
                            )}

                            <Button
                                onClick={placeOrder}
                                disabled={loading}
                                data-testid="place-order-btn"
                                className="w-full rounded-full h-12 bg-primary hover:bg-primary/90 mt-5"
                            >
                                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : codAllowed && paymentMethod === "cod" ? `Place COD Order · Confirm by email` : `Place Order · ${formatINR(total)}`}
                            </Button>
                            <p className="text-[11px] text-muted-foreground text-center mt-3">
                                {codAllowed && paymentMethod === "cod"
                                    ? "We'll email you a one-tap link to confirm your COD order — production starts after you click Confirm."
                                    : "You'll see the Razorpay payment window after clicking. Order confirmation emails are sent automatically."}
                            </p>
                            <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-relaxed space-y-2" data-testid="checkout-no-return">
                                <div><b>Return & refund policy:</b> Every miniature is custom-made from your photos. Once your order has been <b>shipped</b>, we cannot accept returns or issue refunds. Please review your reference photos and design approval carefully before shipping.</div>
                                <div><b>Please note:</b> The final model may not be an exact 100% replica of the preview or the reference photo. Minor variations in facial features, colors, proportions, and details may occur during the creation process, as each piece is handcrafted.</div>
                            </div>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

const Row = ({ label, value, pos }) => (
    <div className="flex justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span className={pos ? "text-emerald-600" : ""}>{value}</span>
    </div>
);

export default Checkout;
